import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import EditorialSelect from "@/components/admin/EditorialSelect";
import DraftWithAIButton from "@/components/ai/DraftWithAIButton";
import {
  ArrowLeft, Upload, Loader2, Send, Copy, ListChecks, X, Heart, CheckCircle2, MessageSquare,
  Image as ImageIcon, Paintbrush, Settings as SettingsIcon, Activity, Users,
  Lock, Download, Star, ExternalLink, Eye, EyeOff, Trash2, FolderPlus, Folder, Plus, Sparkles, Mail, RefreshCw,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "./Dashboard";
import { copyToClipboardWithToast } from "@/lib/clipboard";
import LivePreviewPanel from "./gallery/LivePreviewPanel";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import useLottieOnce from "@/hooks/useLottieOnce";
import goldCheckLottie from "@/assets/lottie/gold-check.json";

const FILE_BASE = process.env.REACT_APP_BACKEND_URL;
const COVER_LAYOUTS = ["center", "left", "novel", "vintage", "frame", "stripe", "divider", "journal", "cinematic"];
const TEMPLATES = [
  { id: "editorial", label: "Editorial", blurb: "Magazine spreads · serif headlines" },
  { id: "mosaic", label: "Mosaic", blurb: "True masonry · dense and immersive" },
  { id: "cinema", label: "Cinema", blurb: "Single-column · dark · slideshow" },
];
const FONT_PRESETS = [
  { id: "editorial", label: "Editorial", blurb: "Cormorant + Inter" },
  { id: "modern", label: "Modern", blurb: "Manrope + Inter" },
  { id: "quiet", label: "Quiet", blurb: "Work Sans + Inter" },
];
const PRESET_ACCENTS = ["#C9A961", "#1C1917", "#A0522D", "#5F7A6F", "#7B5B8B", "#B85042", "#D4A574", "#0F4C5C"];

export default function GalleryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [g, setG] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("photos"); // photos | design | settings | activities
  // Live preview side-panel — auto-opens on the Design tab so customization
  // changes can be watched land. `previewRefreshKey` is bumped on every save so
  // the embedded iframe re-mounts with the latest server state.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  // Phase 5.1 — celebratory one-shot Lottie on gallery share/publish moment.
  // Hooks into sendShare() success additively; the existing toast is preserved.
  const [firePublishBurst, publishBurstSurface] = useLottieOnce(goldCheckLottie, { size: 120 });

  const load = useCallback(async (quiet = false) => {
    // quiet=true refreshes data without unmounting the tab (keeps upload panel state).
    if (quiet !== true) setLoading(true);
    try {
      const { data } = await api.get(`/admin/galleries/${id}`);
      setG(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Auto-scroll to the SelectionsBlock when the URL hash is #selections.
  // Triggered by the "View selected photos" button in the notification email so the
  // studio owner lands directly on the client's favorites instead of having to scroll.
  useEffect(() => {
    if (!g || g.selections?.length === 0) return;
    if (window.location.hash !== "#selections") return;
    setTab("photos");
    const t = setTimeout(() => {
      document.getElementById("selections")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(t);
  }, [g]);

  // G6 — `?tab=favorites` query param (clicked from a notification row)
  // routes the photographer straight to the Favorites tab.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get("tab");
    if (t && ["photos", "design", "settings", "activities", "collaborators", "favorites"].includes(t)) {
      setTab(t);
    }
  }, []);

  async function saveSettings(patch) {
    try {
      const { data } = await api.patch(`/admin/galleries/${id}`, patch);
      setG((prev) => ({ ...prev, ...data }));
      setPreviewRefreshKey((k) => k + 1);
      toast.success("Saved");
      return data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function sendShare() {
    if (!g.customer_email) return toast.error("Add a client email in Settings first");
    if (!window.confirm(`Email gallery link to ${g.customer_email}?`)) return;
    try {
      const { data } = await api.post(`/admin/galleries/${id}/send`);
      toast.success("Share link emailed");
      firePublishBurst();
      try { await copyToClipboardWithToast(data.share_url, "Share URL copied"); } catch (err) { console.warn("clipboard copy failed", err); }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function markDelivered() {
    if (!g.download_password) {
      return toast.error("Set a download password in Settings first, then mark as delivered.");
    }
    if (!window.confirm("Mark this collection as DELIVERED? Clients will be able to download high-res files using the download password.")) return;
    await saveSettings({ status: "delivered", mode: "delivery" });
  }

  if (loading || !g) {
    return <div className="text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>;
  }

  const isFolder = !!g.parent_gallery_id;
  const backTo = isFolder
    ? `/admin/galleries/${g.parent_gallery_id}`
    : g.client_id
      ? `/admin/galleries/clients/${g.client_id}`
      : "/admin/galleries";
  const backLabel = isFolder
    ? `Back to ${g.parent?.name || "collection"}`
    : g.client_id ? "Back to client" : "All collections";

  return (
    <div
      className="space-y-8"
      data-testid="gallery-detail-page"
    >
      {/* Editorial header — breadcrumb eyebrow, massive serif name, status tags, action row */}
      <header className="border-b border-border pb-8">
        <button
          onClick={() => navigate(backTo)}
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="back-to-galleries"
        >
          <ArrowLeft className="w-3 h-3" /> {backLabel}
        </button>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-3 flex items-center gap-1.5">
              {isFolder ? <Folder className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
              {isFolder ? "Folder" : (g.customer_name || "Client collection")}
            </div>
            <h1
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[0.95] tracking-tight truncate"
              data-testid="gallery-title"
            >
              {g.name}.
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <StatusBadge value={g.status} />
              {(g.included_edits || 0) > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] border border-border text-muted-foreground">
                  {g.included_edits} included · ${Number(g.extra_edit_price || 0).toFixed(0)}/extra
                </span>
              )}
              {(g.category_tags || []).map((t) => (
                <span key={t} className="px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] bg-muted/60 text-muted-foreground">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <a
              href={g.share_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground/60 text-[11px] uppercase tracking-[0.22em] transition-colors"
              data-testid="preview-public-btn"
            >
              <Eye className="w-3.5 h-3.5" /> Preview
            </a>
            <button
              onClick={sendShare}
              className="relative inline-flex items-center gap-2 px-4 h-10 bg-foreground text-background text-[11px] uppercase tracking-[0.22em] hover:opacity-90 transition-opacity"
              data-testid="send-gallery-btn"
            >
              <Send className="w-3.5 h-3.5" /> Share
              {publishBurstSurface}
            </button>
            {g.status !== "delivered" && (
              <button
                onClick={markDelivered}
                className="inline-flex items-center gap-2 px-4 h-10 bg-[#C9A961] text-stone-900 text-[11px] uppercase tracking-[0.22em] font-medium hover:opacity-90 transition-opacity"
                data-testid="mark-delivered-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Mark delivered
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Share kit — universal for both top-level collections and folders.
          Renders the link, any passwords, the "Copy" button, AND the new
          "Email it" button that opens a styled-email dialog. */}
      <ShareKitPanel g={g} />

      {/* Folders section — only on top-level collections. Each folder is a self-contained
          sub-gallery with its own slug, password, downloads etc. so the photographer can
          share "Edited" separately from "Proofs". */}
      {!isFolder && <FoldersSection g={g} reload={load} />}

      {/* Selections-received banner — surfaces submissions that would otherwise be
          hidden below the photo grid. Tapping "View" switches to the Photos tab and
          smoothly scrolls down to the SelectionsBlock anchor. */}
      {(g.selections?.length || 0) > 0 && (
        <button
          onClick={() => {
            setTab("photos");
            // Wait a tick for the tab content to mount before scrolling
            setTimeout(() => {
              document.getElementById("selections")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 60);
          }}
          className="w-full text-left bg-rose-50 border border-rose-200 hover:border-rose-300 px-5 py-4 flex items-center gap-3"
          data-testid="selections-banner"
        >
          <Heart className="w-5 h-5 text-rose-500 fill-rose-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-rose-900">
              {g.selections.length} client selection{g.selections.length > 1 ? "s" : ""} received
            </div>
            <div className="text-xs text-rose-700/80 truncate">
              {g.selections.map((s) => `${s.client_name} (${s.photo_ids.length})`).join(" · ")}
            </div>
          </div>
          <span className="text-xs uppercase tracking-[0.18em] text-rose-700 hover:text-rose-900 whitespace-nowrap">View →</span>
        </button>
      )}

      {/* Tabbed layout */}
      <div className="grid lg:grid-cols-[200px_1fr] gap-6">
        {/* Left rail */}
        <aside className="bg-card border border-border h-fit">
          <TabBtn icon={ImageIcon} label="Photos" active={tab === "photos"} onClick={() => setTab("photos")} testid="tab-photos" />
          <TabBtn icon={Paintbrush} label="Design" active={tab === "design"} onClick={() => setTab("design")} testid="tab-design" />
          <TabBtn icon={SettingsIcon} label="Settings" active={tab === "settings"} onClick={() => setTab("settings")} testid="tab-settings" />
          {g.type === "interactive" && (
            <>
              <TabBtn icon={Users} label="Collaborators" active={tab === "collaborators"} onClick={() => setTab("collaborators")} testid="tab-collaborators" />
              <TabBtn icon={Heart} label="Favorites" active={tab === "favorites"} onClick={() => setTab("favorites")} testid="tab-favorites" />
            </>
          )}
          <TabBtn icon={Activity} label="Activities" active={tab === "activities"} onClick={() => setTab("activities")} testid="tab-activities" />
        </aside>

        <div className="min-w-0">
          {tab === "photos" && <PhotosTab g={g} reload={load} />}
          {tab === "design" && !previewOpen && <DesignTab g={g} save={saveSettings} onOpenPreview={() => setPreviewOpen(true)} />}
          {tab === "settings" && <SettingsTab g={g} save={saveSettings} reload={load} onDeleted={() => navigate(backTo)} />}
          {tab === "collaborators" && <CollaboratorsTab g={g} />}
          {tab === "favorites" && <AdminFavoritesTab g={g} />}
          {tab === "activities" && <ActivitiesTab g={g} />}
        </div>
      </div>

      <LivePreviewPanel
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        gallery={g}
        refreshKey={previewRefreshKey}
      >
        <DesignTab g={g} save={saveSettings} inStudio />
      </LivePreviewPanel>
    </div>
  );
}

function TabBtn({ icon: Icon, label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 px-4 py-3 text-sm border-l-2 ${
        active ? "border-foreground bg-muted text-foreground" : "border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground"
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

/* ----------------- PHOTOS TAB ----------------- */
function PhotosTab({ g, reload }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Per-file upload progress: { [fileKey]: { name, loaded, total, status } }
  // status is "queued" | "uploading" | "done" | "failed". The aggregate bar at
  // the top sums loaded/total across all entries for true byte-level progress.
  const [progress, setProgress] = useState({});
  // Files that exhausted their retries — kept so "Retry failed" can re-queue them.
  const [failedFiles, setFailedFiles] = useState([]);
  const [items, setItems] = useState(g.photos || []);
  const [selected, setSelected] = useState(new Set());
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => { setItems(g.photos || []); setSelected(new Set()); }, [g.photos]);

  // Transient failures worth retrying: network drop / pod restart (no response),
  // 429 (rate limit), 5xx (storage hiccup, probe-restart). 4xx like 413/400 are final.
  const RETRY_DELAYS = [1500, 4000, 9000];
  const isTransient = (err) => {
    const st = err?.response?.status;
    return !st || st === 429 || st >= 500;
  };

  async function handleFiles(files) {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setUploading(true);
    setFailedFiles([]);

    // Seed every file as "queued" up-front so the per-file UI renders all of
    // them immediately (rather than popping in as workers pick them up).
    const initial = {};
    list.forEach((f, i) => {
      initial[`${i}-${f.name}`] = { name: f.name, loaded: 0, total: f.size, status: "queued" };
    });
    setProgress(initial);

    const failures = [];
    // Worker-pool pattern: 3 concurrent uploads. Each upload is retried with
    // backoff on transient errors (pod restart, storage hiccup, 429/5xx) so a
    // 150-photo drop survives a blip instead of losing half the batch.
    const queue = list.map((f, i) => ({ file: f, key: `${i}-${f.name}` }));
    const CONCURRENCY = 3;

    async function uploadOnce(file, key) {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/galleries/${g.id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
        onUploadProgress: (evt) => {
          setProgress((p) => ({
            ...p,
            [key]: { ...p[key], loaded: evt.loaded || 0, total: evt.total || file.size },
          }));
        },
      });
    }

    async function worker() {
      while (queue.length > 0) {
        const item = queue.shift();
        if (!item) return;
        const { file, key } = item;
        setProgress((p) => ({ ...p, [key]: { ...p[key], status: "uploading", attempt: 1 } }));
        let lastErr = null;
        for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
          try {
            await uploadOnce(file, key);
            setProgress((p) => ({ ...p, [key]: { ...p[key], loaded: file.size, total: file.size, status: "done" } }));
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
            if (!isTransient(err) || attempt === RETRY_DELAYS.length) break;
            setProgress((p) => ({ ...p, [key]: { ...p[key], loaded: 0, status: "retrying", attempt: attempt + 2 } }));
            await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]));
          }
        }
        if (lastErr) {
          failures.push({ file, reason: formatApiErrorDetail(lastErr.response?.data?.detail) || "Upload failed" });
          setProgress((p) => ({ ...p, [key]: { ...p[key], status: "failed" } }));
        }
      }
    }

    const workers = Array.from({ length: Math.min(CONCURRENCY, list.length) }, () => worker());
    await Promise.all(workers);

    setUploading(false);
    const succeeded = list.length - failures.length;
    if (succeeded > 0) toast.success(`Uploaded ${succeeded} photo${succeeded > 1 ? "s" : ""}`);
    if (failures.length) {
      setFailedFiles(failures.map((f) => f.file));
      const reasons = [...new Set(failures.map((f) => f.reason))].slice(0, 2).join(" · ");
      toast.error(`${failures.length} photo${failures.length > 1 ? "s" : ""} didn't upload after 4 tries — ${reasons}. Use "Retry failed" above.`, { duration: 15000 });
    }
    reload(true);
    if (fileRef.current) fileRef.current.value = "";
    // Hold the progress panel so users see the final state (and the Retry button if needed).
    if (!failures.length) setTimeout(() => setProgress({}), 1500);
  }

  async function deletePhoto(photoId) {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/admin/galleries/${g.id}/photos/${photoId}`);
      toast.success("Photo removed");
      reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  async function setCover(fileId) {
    try {
      await api.patch(`/admin/galleries/${g.id}`, { cover_image: `/api/files/${fileId}` });
      toast.success("Cover updated");
      reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function moveSelectedToChapter(chapterId) {
    // Sentinel "__none__" maps to clearing the chapter assignment.
    const target = chapterId === "__none__" ? "" : chapterId;
    const payload = {
      items: items.map((p, idx) => ({
        id: p.id,
        sort_order: idx,
        chapter_id: selected.has(p.id) ? target : (p.chapter_id ?? null),
      })),
    };
    try {
      await api.post(`/admin/galleries/${g.id}/photos/reorder`, payload);
      toast.success(`Moved ${selected.size} photo${selected.size > 1 ? "s" : ""}`);
      setSelected(new Set());
      reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  async function bulkDelete() {
    if (selected.size === 0) return;
    if (!window.confirm(`Remove ${selected.size} photo${selected.size > 1 ? "s" : ""}?`)) return;
    for (const id of selected) {
      try { await api.delete(`/admin/galleries/${g.id}/photos/${id}`); }
      catch (err) { console.warn("bulk delete failed", err); }
    }
    setSelected(new Set());
    reload();
  }

  async function persistReorder(nextItems) {
    setSavingOrder(true);
    try {
      await api.post(`/admin/galleries/${g.id}/photos/reorder`, {
        items: nextItems.map((p, idx) => ({ id: p.id, sort_order: idx })),
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingOrder(false);
    }
  }

  return (
    <section className="bg-card border border-border p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl">Photos</h2>
          <div className="text-xs text-muted-foreground mt-0.5">
            {items.length} uploaded · drag to reorder{savingOrder ? " · saving…" : ""}
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="upload-photos-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 px-4 h-10 bg-foreground text-background text-xs uppercase tracking-[0.18em] hover:bg-foreground/90 disabled:opacity-60" data-testid="upload-photos-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? (() => {
            const entries = Object.values(progress);
            const totalBytes = entries.reduce((s, e) => s + (e.total || 0), 0);
            const loadedBytes = entries.reduce((s, e) => s + (e.loaded || 0), 0);
            const pct = totalBytes > 0 ? Math.floor((loadedBytes / totalBytes) * 100) : 0;
            const done = entries.filter((e) => e.status === "done").length;
            return `Uploading ${done}/${entries.length} · ${pct}%`;
          })() : "Upload photos"}
        </button>
      </div>

      {/* Per-file upload progress — only visible while uploading or briefly
          after completion. Shows up to 8 rows so a 100-photo drop doesn't
          flood the page; the aggregate is in the button label. */}
      {Object.keys(progress).length > 0 && (
        <div className="mb-5 border border-[color:var(--gold-foil)]/25 bg-card/60 p-4 space-y-2" data-testid="upload-progress-panel">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--gold-base)]">
              {uploading ? "Uploading" : "Uploaded"} {Object.keys(progress).length} photo{Object.keys(progress).length === 1 ? "" : "s"} · 3 parallel · auto-retry
            </div>
            {!uploading && failedFiles.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleFiles(failedFiles)}
                  className="inline-flex items-center gap-1.5 px-3 h-8 bg-rose-500/10 border border-rose-500/40 text-rose-500 text-[10px] uppercase tracking-[0.2em] hover:bg-rose-500/20"
                  data-testid="upload-retry-failed-btn"
                >
                  <Upload className="w-3 h-3" /> Retry failed ({failedFiles.length})
                </button>
                <button
                  onClick={() => { setProgress({}); setFailedFiles([]); }}
                  className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground"
                  data-testid="upload-dismiss-btn"
                >
                  Dismiss
                </button>
              </div>
            )}
          </div>
          {Object.entries(progress)
            .sort(([, a], [, b]) => (a.status === "failed" ? -1 : 0) - (b.status === "failed" ? -1 : 0))
            .slice(0, 8).map(([key, p]) => {
            const pct = p.total > 0 ? Math.min(100, Math.floor((p.loaded / p.total) * 100)) : 0;
            const tone =
              p.status === "done" ? "bg-emerald-500/70" :
              p.status === "failed" ? "bg-rose-500/70" :
              p.status === "retrying" ? "bg-amber-400/70" :
              "bg-[color:var(--gold-base)]";
            return (
              <div key={key} className="flex items-center gap-3 text-xs" data-testid={`upload-row-${key}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="truncate text-foreground/80">{p.name}</span>
                    <span className="font-mono text-[10px] text-muted-foreground ml-3 shrink-0">
                      {p.status === "queued" ? "queued"
                        : p.status === "failed" ? "failed"
                        : p.status === "retrying" ? `retry ${p.attempt}/4`
                        : p.status === "done" ? "✓ done"
                        : `${pct}%`}
                    </span>
                  </div>
                  <div className="h-[2px] bg-foreground/10 overflow-hidden">
                    <div className={`h-full ${tone} transition-all duration-200`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
          {Object.keys(progress).length > 8 && (
            <div className="text-[10px] text-muted-foreground pt-1">+{Object.keys(progress).length - 8} more…</div>
          )}
        </div>
      )}

      {selected.size > 0 && (
        <BulkBar
          count={selected.size}
          chapters={g.chapters || []}
          onClear={() => setSelected(new Set())}
          onMove={moveSelectedToChapter}
          onDelete={bulkDelete}
        />
      )}

      {items.length === 0 ? (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
          className="border border-dashed border-border p-16 text-center text-muted-foreground cursor-pointer hover:border-foreground/40"
          data-testid="photo-drop-zone"
        >
          <Upload className="w-8 h-8 mx-auto text-muted-foreground/40 mb-3" />
          <div className="font-serif text-2xl text-foreground/60">Add the first photos</div>
          <div className="text-sm mt-1">Drag &amp; drop or click — JPG, PNG, WebP, HEIC up to 100 MB each</div>
        </div>
      ) : (
        <SortablePhotoGrid
          items={items}
          onReorder={(next) => { setItems(next); persistReorder(next); }}
          selected={selected}
          onToggleSelect={toggleSelect}
          onSetCover={setCover}
          onDelete={deletePhoto}
          coverFileId={(g.cover_image || "").split("/").pop()}
          chapters={g.chapters || []}
        />
      )}

      {/* Selections inline preview */}
      {g.selections.length > 0 && <SelectionsBlock g={g} />}
    </section>
  );
}

function BulkBar({ count, chapters, onClear, onMove, onDelete }) {
  return (
    <div className="sticky top-2 z-20 mb-5 bg-stone-950 text-stone-50 px-4 h-12 flex items-center justify-between gap-4 shadow-lg" data-testid="bulk-actions-bar">
      <div className="text-xs">
        <strong>{count}</strong> selected
      </div>
      <div className="flex items-center gap-2">
        {chapters.length > 0 && (
          <div className="min-w-[180px]">
            <EditorialSelect
              value=""
              onChange={(v) => onMove(v)}
              options={[
                { value: "__none__", label: "Unsectioned" },
                ...chapters.map((c) => ({ value: c.id, label: c.title })),
              ]}
              placeholder="Move to chapter…"
              data-testid="bulk-move-chapter"
            />
          </div>
        )}
        <button onClick={onDelete} className="px-3 h-8 text-[10px] uppercase tracking-[0.18em] bg-red-500 hover:bg-red-600" data-testid="bulk-delete-btn">
          Delete
        </button>
        <button onClick={onClear} className="px-3 h-8 text-[10px] uppercase tracking-[0.18em] border border-stone-600 hover:border-stone-400" data-testid="bulk-clear-btn">
          Clear
        </button>
      </div>
    </div>
  );
}

function SortablePhotoGrid({ items, onReorder, selected, onToggleSelect, onSetCover, onDelete, coverFileId, chapters }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((p) => p.id === active.id);
    const newIdx = items.findIndex((p) => p.id === over.id);
    onReorder(arrayMove(items, oldIdx, newIdx));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((p) => p.id)} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2" data-testid="photo-grid">
          {items.map((p) => (
            <SortablePhoto
              key={p.id}
              photo={p}
              isCover={coverFileId && coverFileId === p.file_id}
              isSelected={selected.has(p.id)}
              chapter={chapters.find((c) => c.id === p.chapter_id)}
              onToggleSelect={onToggleSelect}
              onSetCover={onSetCover}
              onDelete={onDelete}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortablePhoto({ photo, isCover, isSelected, chapter, onToggleSelect, onSetCover, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: photo.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : "auto",
    opacity: isDragging ? 0.6 : 1,
  };
  // Thumbnail size for the Photos tab grid. Tiles are ~200-260px on screen;
  // `?w=600` is 2× DPR-safe and shrinks a 4MB original to ~35-60KB (~100×
  // smaller wire size). Combined with `loading="lazy"` below, a 259-photo
  // gallery renders the visible tiles almost instantly instead of stalling
  // on huge originals.
  const url = `${FILE_BASE}/api/files/${photo.file_id}?w=600&q=78`;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group aspect-square bg-muted overflow-hidden cursor-grab active:cursor-grabbing ${isSelected ? "ring-2 ring-foreground ring-offset-2" : ""}`}
      data-testid={`photo-tile-${photo.id}`}
      {...attributes}
      {...listeners}
    >
      <img src={url} alt={photo.original_filename} className="w-full h-full object-cover pointer-events-none" loading="lazy" />
      {isCover && <div className="absolute top-1.5 left-1.5 px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] bg-white/95 text-foreground inline-flex items-center gap-1 pointer-events-none"><Star className="w-2.5 h-2.5 fill-foreground" /> cover</div>}
      {chapter && <div className="absolute bottom-1.5 left-1.5 px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] bg-foreground/85 text-background pointer-events-none">{chapter.title}</div>}
      <div className="absolute inset-0 transition-opacity flex items-end justify-between p-1.5 gap-1 bg-black/50 max-md:opacity-100 md:opacity-0 md:group-hover:opacity-100 md:bg-black/30">
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onToggleSelect(photo.id); }}
          className={`px-2 h-7 text-[10px] uppercase tracking-[0.15em] font-medium ${isSelected ? "bg-foreground text-background" : "bg-white/95 hover:bg-white text-stone-900"}`}
          data-testid={`select-photo-${photo.id}`}
        >
          {isSelected ? "Selected" : "Select"}
        </button>
        <div className="flex items-center gap-1">
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onSetCover(photo.file_id); }}
            className="px-2 h-7 text-[10px] uppercase tracking-[0.15em] font-medium bg-white/95 hover:bg-white text-stone-900"
            data-testid={`set-cover-${photo.id}`}
          >
            Cover
          </button>
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onDelete(photo.id); }}
            className="w-7 h-7 bg-destructive text-destructive-foreground hover:bg-destructive/90 flex items-center justify-center"
            data-testid={`delete-photo-${photo.id}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------- DESIGN TAB ----------------- */
function DesignTab({ g, save, onOpenPreview, inStudio = false }) {
  return (
    <section
      className={inStudio ? "space-y-8" : "bg-card border border-border p-6 space-y-10"}
      data-testid={inStudio ? "design-studio-tab" : "design-tab"}
    >
      <DesignToolbar g={g} save={save} onOpenPreview={inStudio ? null : onOpenPreview} inStudio={inStudio} />
      <TemplatePicker g={g} save={save} />
      <ThemeCustomization g={g} save={save} />
      <CoverDesign g={g} save={save} />
      <ChaptersEditor g={g} save={save} />
      <ExtrasPanel g={g} save={save} inStudio={inStudio} />
    </section>
  );
}

function DesignToolbar({ g, save, onOpenPreview, inStudio = false }) {
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  async function askAI() {
    setSuggesting(true);
    setSuggestion(null);
    try {
      const { data } = await api.post(`/admin/galleries/${g.id}/ai-template-suggest`, {});
      setSuggestion({ ...data.suggestion, photos_analyzed: data.photos_analyzed || 0 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI suggestion failed");
    } finally {
      setSuggesting(false);
    }
  }

  async function applySuggestion() {
    if (!suggestion) return;
    const patch = {};
    for (const k of ["template", "accent_color", "font_preset", "cover_layout", "signature_block"]) {
      if (suggestion[k]) patch[k] = suggestion[k];
    }
    await save(patch);
    setSuggestion(null);
    toast.success("Applied AI suggestion");
  }

  return (
    <div
      className={`${inStudio ? "-mx-5 -mt-5 px-5" : "-mx-6 -mt-6 px-6"} py-3 border-b border-border bg-muted/30 flex flex-col gap-3`}
      data-testid="design-toolbar"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground">Gallery design studio</div>
        <div className="flex items-center gap-2">
          <button
            onClick={askAI}
            disabled={suggesting}
            className="inline-flex items-center gap-2 px-4 h-9 text-[11px] uppercase tracking-[0.22em] border border-border hover:border-foreground/60 transition-colors disabled:opacity-60"
            data-testid="ai-suggest-btn"
          >
            {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {suggesting ? "Reading the gallery…" : "AI Suggest"}
          </button>
          {onOpenPreview && (
            <button
              onClick={onOpenPreview}
              className="inline-flex items-center gap-2 px-4 h-9 text-[11px] uppercase tracking-[0.22em] bg-foreground text-background hover:bg-foreground/90 transition-colors"
              data-testid="open-live-preview-btn"
            >
              <Eye className="w-3.5 h-3.5" /> Live preview
            </button>
          )}
        </div>
      </div>
      {suggestion && (
        <AISuggestionCard suggestion={suggestion} onApply={applySuggestion} onDismiss={() => setSuggestion(null)} />
      )}
    </div>
  );
}

function AISuggestionCard({ suggestion, onApply, onDismiss }) {
  const visionCount = suggestion.photos_analyzed || 0;
  return (
    <div className="bg-background border border-foreground/20 p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="ai-suggestion-card">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] bg-foreground text-background">
            <Sparkles className="w-3 h-3" /> AI
          </span>
          {visionCount > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] bg-emerald-100 text-emerald-800 border border-emerald-200"
              title="Claude looked at this many photos before recommending"
              data-testid="ai-vision-badge"
            >
              <Eye className="w-3 h-3" /> Vision · {visionCount} {visionCount === 1 ? "photo" : "photos"}
            </span>
          )}
          <code className="text-[11px] uppercase tracking-[0.18em]">
            {suggestion.template} · {suggestion.cover_layout} · {suggestion.font_preset}
          </code>
          <div className="inline-flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">accent</span>
            <span className="w-4 h-4 inline-block border border-border" style={{ backgroundColor: suggestion.accent_color }} />
            <code className="text-[10px] uppercase tracking-[0.18em]">{suggestion.accent_color}</code>
          </div>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          {suggestion.reasoning}
        </p>
        {suggestion.signature_block && (
          <p className="text-[11px] text-muted-foreground italic mt-2 whitespace-pre-line">
            “{suggestion.signature_block}”
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={onDismiss} className="px-3 h-9 text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground" data-testid="ai-suggestion-dismiss">
          Dismiss
        </button>
        <button onClick={onApply} className="px-4 h-9 bg-foreground text-background text-[11px] uppercase tracking-[0.22em] hover:bg-foreground/90" data-testid="ai-suggestion-apply">
          Apply
        </button>
      </div>
    </div>
  );
}

function TemplatePicker({ g, save }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-serif text-2xl">Template</h2>
        <p className="text-xs text-muted-foreground mt-1">Pick the visual identity your client sees on the public gallery.</p>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3" data-testid="template-picker">
        {TEMPLATES.map((t) => {
          const active = (g.template || "editorial") === t.id;
          return (
            <button
              key={t.id}
              onClick={() => save({ template: t.id })}
              className={`text-left p-5 border-2 transition-all ${active ? "border-foreground bg-muted/50" : "border-border hover:border-foreground/40"}`}
              data-testid={`template-${t.id}`}
            >
              <TemplateThumbnail id={t.id} photos={g.photos || []} />
              <div className="font-serif text-lg mt-3">{t.label}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{t.blurb}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TemplateThumbnail({ id, photos = [] }) {
  // Real gallery photos inside each layout mockup; gray blocks only when the
  // gallery has no photos yet.
  const thumb = (i, cls = "") => {
    const p = photos[i % Math.max(photos.length, 1)];
    if (!p) return <div className={`bg-stone-300 ${cls}`} />;
    return (
      <img
        src={`${FILE_BASE}/api/files/${p.file_id}?w=200&q=70`}
        alt=""
        loading="lazy"
        className={`object-cover w-full h-full ${cls}`}
      />
    );
  };
  if (id === "editorial") {
    return (
      <div className="aspect-[4/3] bg-stone-100 p-2 grid grid-cols-3 grid-rows-3 gap-1">
        <div className="col-span-3 row-span-2 overflow-hidden">{thumb(0)}</div>
        <div className="overflow-hidden">{thumb(1)}</div>
        <div className="overflow-hidden">{thumb(2)}</div>
        <div className="overflow-hidden">{thumb(3)}</div>
      </div>
    );
  }
  if (id === "mosaic") {
    return (
      <div className="aspect-[4/3] bg-stone-100 p-1.5 flex gap-1">
        <div className="flex-1 flex flex-col gap-1">
          <div className="h-2/5 overflow-hidden">{thumb(0)}</div>
          <div className="h-1/4 overflow-hidden">{thumb(3)}</div>
          <div className="h-1/3 overflow-hidden">{thumb(6)}</div>
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <div className="h-1/3 overflow-hidden">{thumb(1)}</div>
          <div className="h-2/5 overflow-hidden">{thumb(4)}</div>
          <div className="h-1/4 overflow-hidden">{thumb(7)}</div>
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <div className="h-1/4 overflow-hidden">{thumb(2)}</div>
          <div className="h-1/2 overflow-hidden">{thumb(5)}</div>
          <div className="h-1/5 overflow-hidden">{thumb(8)}</div>
        </div>
      </div>
    );
  }
  if (id === "cinema") {
    return (
      <div className="aspect-[4/3] bg-stone-950 p-3 flex flex-col items-center justify-center gap-2">
        <div className="w-full h-1/2 overflow-hidden">{photos.length ? thumb(0) : <div className="w-full h-full bg-stone-700" />}</div>
        <div className="w-3/4 h-1.5 bg-[#C9A961]" />
        <div className="w-1/2 h-1 bg-stone-600" />
      </div>
    );
  }
  return <div className="aspect-[4/3] bg-stone-100" />;
}

function ThemeCustomization({ g, save }) {
  const [accent, setAccent] = useState(g.accent_color || "#C9A961");
  const [overlayOpacity, setOverlayOpacity] = useState(g.cover_overlay_opacity ?? 0.3);
  const [overlayTone, setOverlayTone] = useState(g.cover_overlay_tone || "dark");

  useEffect(() => setAccent(g.accent_color || "#C9A961"), [g.accent_color]);
  useEffect(() => setOverlayOpacity(g.cover_overlay_opacity ?? 0.3), [g.cover_overlay_opacity]);
  useEffect(() => setOverlayTone(g.cover_overlay_tone || "dark"), [g.cover_overlay_tone]);

  return (
    <div className="space-y-5 border-t border-border pt-8">
      <div>
        <h2 className="font-serif text-2xl">Theme &amp; typography</h2>
        <p className="text-xs text-muted-foreground mt-1">Drive buttons, accents, and overlay tone per gallery.</p>
      </div>

      <div className="space-y-3">
        <div className="eyebrow">Accent color</div>
        <div className="flex flex-wrap items-center gap-2" data-testid="accent-color-picker">
          {PRESET_ACCENTS.map((c) => (
            <button
              key={c}
              onClick={() => { setAccent(c); save({ accent_color: c }); }}
              style={{ backgroundColor: c }}
              className={`w-9 h-9 transition-transform hover:scale-110 ${accent === c ? "ring-2 ring-foreground ring-offset-2" : ""}`}
              aria-label={`Accent ${c}`}
              data-testid={`accent-${c.replace("#", "")}`}
            />
          ))}
          <label className="inline-flex items-center gap-2 ml-1 cursor-pointer">
            <input
              type="color"
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
              onBlur={(e) => save({ accent_color: e.target.value })}
              className="w-9 h-9 border border-border cursor-pointer"
              data-testid="accent-custom-input"
            />
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Custom</span>
          </label>
        </div>
      </div>

      <div className="space-y-2">
        <div className="eyebrow">Font preset</div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-2" data-testid="font-preset-picker">
          {FONT_PRESETS.map((f) => {
            const active = (g.font_preset || "editorial") === f.id;
            return (
              <button
                key={f.id}
                onClick={() => save({ font_preset: f.id })}
                className={`text-left p-3 border-2 transition-all ${active ? "border-foreground bg-muted/50" : "border-border hover:border-foreground/40"}`}
                data-testid={`font-${f.id}`}
              >
                <div className="font-serif text-base">{f.label}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{f.blurb}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="eyebrow mb-2">Cover overlay opacity ({Math.round(overlayOpacity * 100)}%)</div>
          <input
            type="range"
            min="0"
            max="0.85"
            step="0.05"
            value={overlayOpacity}
            onChange={(e) => setOverlayOpacity(parseFloat(e.target.value))}
            onMouseUp={(e) => save({ cover_overlay_opacity: parseFloat(e.target.value) })}
            onTouchEnd={(e) => save({ cover_overlay_opacity: parseFloat(e.target.value) })}
            className="w-full"
            data-testid="overlay-opacity-slider"
          />
        </div>
        <div>
          <div className="eyebrow mb-2">Overlay tone</div>
          <div className="flex gap-2">
            {["dark", "light"].map((t) => (
              <button
                key={t}
                onClick={() => { setOverlayTone(t); save({ cover_overlay_tone: t }); }}
                className={`flex-1 h-10 text-xs uppercase tracking-[0.18em] border ${overlayTone === t ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/40"}`}
                data-testid={`overlay-tone-${t}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CoverDesign({ g, save }) {
  const [layout, setLayout] = useState(g.cover_layout || "center");
  useEffect(() => setLayout(g.cover_layout || "center"), [g.cover_layout]);
  async function apply(l) { setLayout(l); await save({ cover_layout: l }); }
  return (
    <div className="space-y-8 border-t border-border pt-8">
      <CoverImagePicker g={g} save={save} />
      <CoverFocalPicker g={g} save={save} />
      <div className="space-y-4">
        <div>
          <h2 className="font-serif text-2xl">Cover layout</h2>
          <p className="text-xs text-muted-foreground mt-1">Hero composition shown above the photo grid.</p>
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(105px,1fr))] gap-3" data-testid="cover-layout-picker">
          {COVER_LAYOUTS.map((l) => (
            <button
              key={l}
              onClick={() => apply(l)}
              className={`group bg-stone-50 border ${layout === l ? "border-foreground ring-1 ring-foreground" : "border-border hover:border-foreground/60"}`}
              data-testid={`cover-layout-${l}`}
            >
              <div className="aspect-[3/4] p-3 bg-stone-100 relative overflow-hidden">
                <CoverPreviewMini layout={l} coverImage={g.cover_image} />
              </div>
              <div className="p-2 text-xs capitalize text-center border-t border-border">{l}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Cover image picker — tiny fast thumbnails (400px bucket) so the photographer
   never has to scroll the heavy Photos tab just to change the cover. */
function CoverImagePicker({ g, save }) {
  const photos = g.photos || [];
  const currentFileId = (g.cover_image || "").split("/").pop().split("?")[0];
  if (!photos.length) return null;
  return (
    <div className="space-y-3">
      <div>
        <h2 className="font-serif text-2xl">Cover image</h2>
        <p className="text-xs text-muted-foreground mt-1">Tap a photo to make it the hero.</p>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(72px,1fr))] gap-1.5 max-h-64 overflow-y-auto pr-1" data-testid="cover-image-picker">
        {photos.map((p) => {
          const isCover = p.file_id === currentFileId;
          return (
            <button
              key={p.id}
              onClick={() => save({ cover_image: `/api/files/${p.file_id}` })}
              className={`relative w-full pt-[100%] overflow-hidden border-2 transition-colors ${isCover ? "border-foreground" : "border-transparent hover:border-foreground/50"}`}
              data-testid={`cover-pick-${p.id}`}
              title={p.original_filename || ""}
            >
              <img
                src={`${FILE_BASE}/api/files/${p.file_id}?w=200&q=70`}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {isCover && (
                <span className="absolute inset-x-0 bottom-0 bg-foreground text-background text-[8px] uppercase tracking-[0.2em] text-center py-0.5">
                  Cover
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* Focal point — click anywhere on the cover to choose which part of the photo
   stays centered in the hero crop. Saved as CSS object-position percentages. */
function CoverFocalPicker({ g, save }) {
  const fx = g.cover_focal_x ?? 50;
  const fy = g.cover_focal_y ?? 50;
  const [aiBusy, setAiBusy] = useState(false);
  const coverUrl = g.cover_image || (g.photos?.[0] ? `/api/files/${g.photos[0].file_id}` : "");
  if (!coverUrl) return null;
  const src = `${FILE_BASE}${coverUrl}${coverUrl.includes("?") ? "&" : "?"}w=800&q=78`;

  function pick(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    save({ cover_focal_x: Math.max(0, Math.min(100, x)), cover_focal_y: Math.max(0, Math.min(100, y)) });
  }

  async function aiFocal() {
    setAiBusy(true);
    try {
      // Reuse the site auto-focal endpoint (Gemini vision). Send the 1200px
      // variant — pre-generated at upload, well under the 8 MB cap.
      const imageUrl = `${coverUrl}${coverUrl.includes("?") ? "&" : "?"}w=1200&q=80`;
      const { data } = await api.post(`/admin/site/auto-focal`, { image_url: imageUrl });
      await save({ cover_focal_x: data.x, cover_focal_y: data.y });
      if (data.detected) toast.success("Focal point set on the subject's face");
      else toast.info(data.reason || "No clear face found — kept center");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI focal failed");
    } finally {
      setAiBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl">Focal point</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Click where the subject is — the hero crop keeps that spot in view on every screen size.
          </p>
        </div>
        {(fx !== 50 || fy !== 50) && (
          <button
            onClick={() => save({ cover_focal_x: 50, cover_focal_y: 50 })}
            className="shrink-0 text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground underline underline-offset-4"
            data-testid="focal-reset-btn"
          >
            Reset center
          </button>
        )}
      </div>
      <div
        className="relative w-full max-w-sm cursor-crosshair select-none border border-border overflow-hidden"
        onClick={pick}
        data-testid="cover-focal-picker"
      >
        <img src={src} alt="Cover focal" className="w-full h-auto block pointer-events-none" draggable={false} />
        <span
          className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-white shadow-[0_0_0_2px_rgba(0,0,0,0.55)] pointer-events-none"
          style={{ left: `${fx}%`, top: `${fy}%` }}
          data-testid="focal-marker"
        />
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="focal-coords">
          Focal · {Math.round(fx)}% / {Math.round(fy)}%
        </div>
        <button
          onClick={aiFocal}
          disabled={aiBusy}
          className="inline-flex items-center gap-2 px-3 h-8 text-[10px] uppercase tracking-[0.22em] border border-border hover:border-foreground/60 transition-colors disabled:opacity-60"
          data-testid="ai-focal-btn"
        >
          {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {aiBusy ? "Finding the face…" : "AI focal"}
        </button>
      </div>
    </div>
  );
}

function ChaptersEditor({ g, save }) {
  const [chapters, setChapters] = useState(g.chapters || []);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setChapters(g.chapters || []); setDirty(false); }, [g.chapters]);

  function add() {
    setChapters((c) => [...c, { id: Math.random().toString(36).slice(2, 10), title: "New chapter", intro: "", sort_order: c.length }]);
    setDirty(true);
  }
  function update(idx, patch) {
    setChapters((c) => c.map((ch, i) => (i === idx ? { ...ch, ...patch } : ch)));
    setDirty(true);
  }
  function remove(idx) {
    setChapters((c) => c.filter((_, i) => i !== idx));
    setDirty(true);
  }
  function persist() {
    save({ chapters: chapters.map((c, i) => ({ ...c, sort_order: i })) });
    setDirty(false);
  }

  return (
    <div className="space-y-4 border-t border-border pt-8">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-serif text-2xl">Chapters</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Section breaks rendered between photo groups. Assign photos to a chapter from the Photos tab.
          </p>
        </div>
        <button onClick={add} className="px-4 h-9 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60" data-testid="add-chapter-btn">
          + Chapter
        </button>
      </div>
      {chapters.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No chapters yet — photos render as one continuous flow.</p>
      )}
      <div className="space-y-3" data-testid="chapter-list">
        {chapters.map((ch, idx) => (
          <div key={ch.id} className="border border-border p-3 space-y-2" data-testid={`chapter-${ch.id}`}>
            <div className="flex items-center gap-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground w-12 tabular-nums">CH {String(idx + 1).padStart(2, "0")}</div>
              <input
                value={ch.title}
                onChange={(e) => update(idx, { title: e.target.value })}
                placeholder="Chapter title"
                className="flex-1 bg-transparent border-b border-border py-1.5 text-sm outline-none focus:border-foreground"
                data-testid={`chapter-title-${ch.id}`}
              />
              <button onClick={() => remove(idx)} className="text-muted-foreground hover:text-destructive" data-testid={`chapter-remove-${ch.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <textarea
              value={ch.intro}
              onChange={(e) => update(idx, { intro: e.target.value })}
              placeholder="Optional intro paragraph"
              rows={2}
              className="w-full bg-transparent border border-border p-2 text-xs outline-none focus:border-foreground resize-none"
              data-testid={`chapter-intro-${ch.id}`}
            />
          </div>
        ))}
      </div>
      {dirty && (
        <div className="flex justify-end">
          <button onClick={persist} className="px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em]" data-testid="save-chapters-btn">
            Save chapters
          </button>
        </div>
      )}
    </div>
  );
}

function ExtrasPanel({ g, save, inStudio = false }) {
  const [signature, setSignature] = useState(g.signature_block || "");
  const [watermark, setWatermark] = useState(!!g.watermark_enabled);
  const [slideshowAuto, setSlideshowAuto] = useState(!!g.slideshow_autoplay);
  const [musicUrl, setMusicUrl] = useState(g.music_external_url || "");
  const musicRef = useRef(null);
  const [musicUploading, setMusicUploading] = useState(false);

  useEffect(() => setSignature(g.signature_block || ""), [g.signature_block]);
  useEffect(() => setWatermark(!!g.watermark_enabled), [g.watermark_enabled]);
  useEffect(() => setSlideshowAuto(!!g.slideshow_autoplay), [g.slideshow_autoplay]);
  useEffect(() => setMusicUrl(g.music_external_url || ""), [g.music_external_url]);

  async function saveMusicUrl() {
    const url = musicUrl.trim();
    if (url && !/^https?:\/\/.+/i.test(url)) {
      toast.error("Paste a full link starting with https:// — it must point directly to an audio file (.mp3/.m4a). YouTube links won't play.");
      return;
    }
    await save({ music_external_url: url });
    if (url) toast.success("Music link saved");
  }

  async function uploadMusic(file) {
    if (!file) return;
    setMusicUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/galleries/${g.id}/music`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Music uploaded");
      // Saving null patch just to trigger reload via parent; the file is already linked server-side
      await save({});
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setMusicUploading(false); if (musicRef.current) musicRef.current.value = ""; }
  }
  function removeMusic() {
    if (!window.confirm("Remove background music from this gallery?")) return;
    save({ music_file_id: "", music_filename: "" });
  }

  return (
    <div className="space-y-5 border-t border-border pt-8">
      <div>
        <h2 className="font-serif text-2xl">Finishing touches</h2>
        <p className="text-xs text-muted-foreground mt-1">Signature, watermark, slideshow music — the small things that feel premium.</p>
      </div>
      <div className={`grid gap-5 ${inStudio ? "grid-cols-1" : "sm:grid-cols-2"}`}>
        <label className="block">
          <span className="eyebrow">Signature block</span>
          <textarea
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            onBlur={() => save({ signature_block: signature })}
            rows={4}
            placeholder="Photographed by [Studio Name]…"
            className="mt-1 w-full bg-transparent border border-border p-2 outline-none focus:border-foreground text-sm resize-none"
            data-testid="signature-block-input"
          />
          <p className="text-[10px] text-muted-foreground mt-1">Appears in the gallery footer. Leave empty for a quiet default.</p>
        </label>
        <div className="space-y-4">
          <ToggleRow
            label="Watermark previews"
            description="Adds a 'Preview' overlay to non-downloaded photos so screenshots stay watermarked."
            on={watermark}
            onChange={(v) => { setWatermark(v); save({ watermark_enabled: v }); }}
            testid="watermark-toggle"
          />
          <ToggleRow
            label="Auto-play slideshow (Cinema)"
            description="Starts the Cinema slideshow on load. Music begins muted until the visitor unmutes."
            on={slideshowAuto}
            onChange={(v) => { setSlideshowAuto(v); save({ slideshow_autoplay: v }); }}
            testid="slideshow-autoplay-toggle"
          />
          <div>
            <div className="eyebrow mb-2">Background music</div>
            {g.music_filename ? (
              <div className="flex items-center justify-between gap-3 bg-muted/40 border border-border px-3 py-2">
                <div className="text-xs truncate font-mono">{g.music_filename}</div>
                <button onClick={removeMusic} className="text-xs text-muted-foreground hover:text-destructive" data-testid="remove-music-btn">
                  Remove
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <input ref={musicRef} type="file" accept="audio/*" className="hidden" onChange={(e) => uploadMusic(e.target.files?.[0])} data-testid="music-input" />
                <button
                  onClick={() => musicRef.current?.click()}
                  disabled={musicUploading}
                  className="w-full px-4 h-10 border border-dashed border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em] disabled:opacity-60"
                  data-testid="upload-music-btn"
                >
                  {musicUploading ? "Uploading…" : "+ Upload mp3 / m4a"}
                </button>
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    value={musicUrl}
                    onChange={(e) => setMusicUrl(e.target.value)}
                    onBlur={saveMusicUrl}
                    onKeyDown={(e) => { if (e.key === "Enter") saveMusicUrl(); }}
                    placeholder="…or paste a direct audio link (.mp3)"
                    className="flex-1 min-w-0 bg-transparent border border-border px-2 h-9 text-xs outline-none focus:border-foreground"
                    data-testid="music-url-input"
                  />
                  {g.music_external_url && (
                    <button
                      onClick={() => { setMusicUrl(""); save({ music_external_url: "" }); }}
                      className="text-xs text-muted-foreground hover:text-destructive shrink-0"
                      data-testid="clear-music-url-btn"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground leading-relaxed">
                  Needs a direct file link (ends in .mp3/.m4a). YouTube/Spotify links can't play in the background — browsers and their terms don't allow it.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, description, on, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{description}</div>
      </div>
      <Toggle on={on} onChange={onChange} testid={testid} />
    </div>
  );
}

function CoverPreviewMini({ layout, coverImage }) {
  const bg = coverImage ? `url(${FILE_BASE}${coverImage})` : "linear-gradient(135deg,#d6d3d1,#a8a29e)";
  switch (layout) {
    case "center":
      return <div className="w-full h-full bg-cover bg-center flex items-center justify-center" style={{ backgroundImage: bg }}>
        <div className="text-[10px] tracking-widest bg-white/80 px-2 py-1">TITLE</div>
      </div>;
    case "left":
      return <div className="w-full h-full bg-cover bg-center flex items-end" style={{ backgroundImage: bg }}>
        <div className="text-[10px] tracking-widest bg-white/80 px-2 py-1 m-1">TITLE</div>
      </div>;
    case "novel":
      return <div className="w-full h-full flex items-end">
        <div className="w-2/3 h-2/3 bg-cover bg-center" style={{ backgroundImage: bg }} />
        <div className="flex-1 text-[8px] p-1">TITLE</div>
      </div>;
    case "vintage":
      return <div className="w-full h-full bg-cover bg-center flex items-end relative" style={{ backgroundImage: bg }}>
        <div className="absolute inset-x-2 bottom-1 text-[10px] text-white text-center border-t border-white pt-1">TITLE</div>
      </div>;
    case "frame":
      return <div className="w-full h-full bg-white p-2 flex items-center justify-center">
        <div className="w-full h-full bg-cover bg-center relative border border-stone-700" style={{ backgroundImage: bg }}>
          <div className="absolute inset-x-0 bottom-1 text-[10px] tracking-widest text-white text-center">TITLE</div>
        </div>
      </div>;
    case "stripe":
      return <div className="w-full h-full bg-cover bg-center relative" style={{ backgroundImage: bg }}>
        <div className="absolute inset-x-0 top-2 h-px bg-white/80" />
        <div className="absolute inset-x-0 bottom-2 h-px bg-white/80" />
        <div className="absolute inset-0 flex items-center justify-center text-[10px] tracking-widest text-white">TITLE</div>
      </div>;
    case "divider":
      return <div className="w-full h-full flex flex-col">
        <div className="flex-1 bg-cover bg-center" style={{ backgroundImage: bg }} />
        <div className="h-1/4 bg-white text-[10px] flex items-center justify-center tracking-widest">TITLE</div>
      </div>;
    case "journal":
      return <div className="w-full h-full flex">
        <div className="w-1/2 bg-cover bg-center" style={{ backgroundImage: bg }} />
        <div className="w-1/2 bg-white text-[10px] flex items-center justify-center tracking-widest">TITLE</div>
      </div>;
    case "cinematic":
      return <div className="w-full h-full bg-cover bg-center relative" style={{ backgroundImage: bg }}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-2 left-2 text-[9px] tracking-widest text-white">TITLE</div>
        <div className="absolute bottom-2 right-2 w-3 h-px bg-[#C9A961]" />
      </div>;
    default:
      return null;
  }
}

function CoverPreview({ layout, coverImage, name, studioName, customerName }) {
  return (
    <div className="aspect-[16/9]">
      <CoverHero layout={layout} coverImage={coverImage} name={name} studioName={studioName} customerName={customerName} preview />
    </div>
  );
}

// Reusable cover renderer (also used by public Gallery)
export function CoverHero({ layout, coverImage, name, studioName, customerName, preview = false }) {
  const bg = coverImage ? `${FILE_BASE}${coverImage}` : null;
  const bgStyle = bg ? { backgroundImage: `url(${bg})` } : { background: "linear-gradient(135deg,#d6d3d1,#a8a29e)" };
  const text = (
    <>
      <div className="text-[10px] tracking-[0.3em] uppercase opacity-80">{studioName}</div>
      <div className={`font-serif font-light ${preview ? "text-3xl" : "text-5xl md:text-6xl"} mt-2`}>{name}</div>
      {customerName && <div className="text-xs mt-2 opacity-80">For {customerName}</div>}
    </>
  );

  if (layout === "center") {
    return (
      <div className="relative w-full h-full bg-cover bg-center" style={bgStyle}>
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative h-full flex items-center justify-center text-center text-white px-6">
          <div>{text}</div>
        </div>
      </div>
    );
  }
  if (layout === "left") {
    return (
      <div className="relative w-full h-full bg-cover bg-center" style={bgStyle}>
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
        <div className="relative h-full flex items-end justify-start text-left text-white px-8 pb-8">
          <div>{text}</div>
        </div>
      </div>
    );
  }
  if (layout === "novel") {
    return (
      <div className="w-full h-full flex bg-stone-50">
        <div className="w-2/3 bg-cover bg-center" style={bgStyle} />
        <div className="w-1/3 flex items-end p-6 text-stone-900">
          <div>{text}</div>
        </div>
      </div>
    );
  }
  if (layout === "vintage") {
    return (
      <div className="relative w-full h-full bg-cover bg-center" style={bgStyle}>
        <div className="absolute inset-0 bg-black/35" />
        <div className="relative h-full flex flex-col items-center justify-end text-center text-white px-6 pb-10">
          <div className="border-t border-b border-white/80 py-4 px-8 max-w-3xl w-full">{text}</div>
        </div>
      </div>
    );
  }
  if (layout === "frame") {
    return (
      <div className="w-full h-full bg-stone-50 p-4 sm:p-6">
        <div className="relative w-full h-full bg-cover bg-center border-2 border-stone-900/10" style={bgStyle}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative h-full flex items-end justify-center text-center text-white px-6 pb-6">
            <div>{text}</div>
          </div>
        </div>
      </div>
    );
  }
  if (layout === "stripe") {
    return (
      <div className="relative w-full h-full bg-cover bg-center" style={bgStyle}>
        <div className="absolute inset-0 bg-black/25" />
        <div className="absolute inset-x-8 top-6 h-px bg-white/70" />
        <div className="absolute inset-x-8 bottom-6 h-px bg-white/70" />
        <div className="relative h-full flex items-center justify-center text-center text-white px-6">
          <div>{text}</div>
        </div>
      </div>
    );
  }
  if (layout === "divider") {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="flex-1 bg-cover bg-center" style={bgStyle} />
        <div className="h-1/4 bg-stone-50 flex items-center justify-center text-center text-stone-900 px-6">
          <div>{text}</div>
        </div>
      </div>
    );
  }
  if (layout === "journal") {
    return (
      <div className="w-full h-full flex">
        <div className="w-1/2 bg-cover bg-center" style={bgStyle} />
        <div className="w-1/2 bg-stone-50 flex items-center justify-center text-center px-6 text-stone-900">
          <div>{text}</div>
        </div>
      </div>
    );
  }
  return null;
}

/* ----------------- SETTINGS TAB ----------------- */
function SettingsTab({ g, save, reload, onDeleted }) {
  const [section, setSection] = useState("general"); // general | privacy | download | favorite

  const sections = [
    { key: "general", label: "General", icon: SettingsIcon },
    { key: "privacy", label: "Privacy", icon: Lock },
    { key: "download", label: "Download", icon: Download },
    { key: "favorite", label: "Favorite", icon: Heart },
  ];

  async function deleteGallery() {
    if (!window.confirm(`Permanently delete "${g.name}" and all its photos? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/galleries/${g.id}`);
      toast.success("Collection deleted");
      onDeleted();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  return (
    <section className="bg-card border border-border">
      <div className="grid sm:grid-cols-[180px_1fr]">
        <nav className="border-b sm:border-b-0 sm:border-r border-border">
          {sections.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => setSection(s.key)}
                data-testid={`settings-sub-${s.key}`}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm border-l-2 ${
                  section === s.key ? "border-foreground bg-muted" : "border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`}
              >
                <Icon className="w-4 h-4" /> {s.label}
              </button>
            );
          })}
        </nav>

        <div className="p-6">
          {section === "general" && <GeneralSettings g={g} save={save} reload={reload} onDelete={deleteGallery} />}
          {section === "privacy" && <PrivacySettings g={g} save={save} />}
          {section === "download" && <DownloadSettings g={g} save={save} />}
          {section === "favorite" && <FavoriteSettings g={g} save={save} />}
        </div>
      </div>
    </section>
  );
}

function GeneralSettings({ g, save, onDelete }) {
  const [form, setForm] = useState({
    name: g.name, customer_name: g.customer_name || "", customer_email: g.customer_email || "",
    welcome_message: g.welcome_message || "",
    category_tags: (g.category_tags || []).join(", "),
    expires_at: g.expires_at ? new Date(g.expires_at).toISOString().slice(0, 10) : "",
  });
  useEffect(() => {
    setForm({
      name: g.name, customer_name: g.customer_name || "", customer_email: g.customer_email || "",
      welcome_message: g.welcome_message || "",
      category_tags: (g.category_tags || []).join(", "),
      expires_at: g.expires_at ? new Date(g.expires_at).toISOString().slice(0, 10) : "",
    });
  }, [g]);

  async function submit() {
    const tags = form.category_tags.split(",").map((s) => s.trim()).filter(Boolean);
    const patch = {
      name: form.name, customer_name: form.customer_name, customer_email: form.customer_email,
      welcome_message: form.welcome_message,
      category_tags: tags,
    };
    // expires_at: convert YYYY-MM-DD to end-of-day ISO; empty string clears it
    if (form.expires_at) {
      patch.expires_at = new Date(`${form.expires_at}T23:59:59`).toISOString();
    } else {
      patch.expires_at = "";
    }
    await save(patch);
  }

  return (
    <div className="space-y-4">
      <h3 className="font-serif text-xl">General settings</h3>
      <In label="Collection name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="set-general-name" />
      <div className="grid sm:grid-cols-2 gap-3">
        <In label="Client name" value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} testid="set-general-client-name" />
        <In label="Client email" value={form.customer_email} onChange={(v) => setForm({ ...form, customer_email: v })} testid="set-general-client-email" />
      </div>
      <In label="Category tags (comma-separated)" value={form.category_tags} onChange={(v) => setForm({ ...form, category_tags: v })} testid="set-general-tags" placeholder="maternity, family, brand" />
      <In label="Auto-expire date" type="date" value={form.expires_at} onChange={(v) => setForm({ ...form, expires_at: v })} testid="set-general-expires" />
      <label className="block">
        <span className="eyebrow flex items-center justify-between gap-3">
          Welcome message
          <DraftWithAIButton
            purpose="gallery_welcome"
            context={`Gallery: ${form.name || "(untitled)"}${form.category_tags ? ` · Tags: ${form.category_tags}` : ""}`}
            onDraft={(text) => setForm({ ...form, welcome_message: text })}
            testid="draft-ai-gallery-welcome"
          />
        </span>
        <textarea rows={3} value={form.welcome_message} onChange={(e) => setForm({ ...form, welcome_message: e.target.value })} className="mt-1 w-full bg-transparent border border-border p-2 outline-none focus:border-foreground text-sm" data-testid="set-general-welcome" />
      </label>
      <div className="flex justify-between pt-3 border-t border-border">
        <button onClick={onDelete} className="inline-flex items-center gap-1.5 px-3 h-9 text-xs uppercase tracking-[0.18em] border border-destructive/40 text-destructive hover:bg-destructive/5" data-testid="delete-gallery-btn">
          <Trash2 className="w-3.5 h-3.5" /> Delete collection
        </button>
        <button onClick={submit} className="px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em]" data-testid="save-general-btn">Save</button>
      </div>
    </div>
  );
}

function PrivacySettings({ g, save }) {
  const [form, setForm] = useState({ password: g.password || "", max_selections: g.max_selections || 0 });
  useEffect(() => setForm({ password: g.password || "", max_selections: g.max_selections || 0 }), [g]);
  return (
    <div className="space-y-4">
      <h3 className="font-serif text-xl">Privacy</h3>
      <p className="text-xs text-muted-foreground">Galleries always require a view password. Share it with your client by email.</p>
      <In label="View password *" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="set-privacy-password" />
      <In label="Max selections (0 = unlimited)" type="number" value={form.max_selections} onChange={(v) => setForm({ ...form, max_selections: v })} testid="set-privacy-max-sel" />
      <div className="flex justify-end pt-3 border-t border-border">
        <button onClick={() => save({ password: form.password, max_selections: Number(form.max_selections) || 0 })} className="px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em]" data-testid="save-privacy-btn">Save</button>
      </div>
    </div>
  );
}

function DownloadSettings({ g, save }) {
  const [form, setForm] = useState({
    downloads_enabled: g.downloads_enabled !== false,
    download_password: g.download_password || "",
  });
  useEffect(() => setForm({ downloads_enabled: g.downloads_enabled !== false, download_password: g.download_password || "" }), [g]);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl">Download</h3>
        <Toggle on={form.downloads_enabled} onChange={(v) => setForm({ ...form, downloads_enabled: v })} testid="set-download-toggle" />
      </div>
      <p className="text-xs text-muted-foreground">Clients can only download once the collection is marked as <strong>delivered</strong> and they enter the download password below.</p>
      <In label="Download password" value={form.download_password} onChange={(v) => setForm({ ...form, download_password: v })} testid="set-download-password" />
      <div className="flex justify-end pt-3 border-t border-border">
        <button onClick={() => save({ downloads_enabled: form.downloads_enabled, download_password: form.download_password })} className="px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em]" data-testid="save-download-btn">Save</button>
      </div>
    </div>
  );
}

function FavoriteSettings({ g, save }) {
  const [form, setForm] = useState({
    favorites_enabled: g.favorites_enabled !== false,
    included_edits: g.included_edits || 0,
    extra_edit_price: g.extra_edit_price ?? 35,
  });
  useEffect(() => setForm({
    favorites_enabled: g.favorites_enabled !== false,
    included_edits: g.included_edits || 0,
    extra_edit_price: g.extra_edit_price ?? 35,
  }), [g]);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl">Favorites &amp; edits</h3>
        <Toggle on={form.favorites_enabled} onChange={(v) => setForm({ ...form, favorites_enabled: v })} testid="set-fav-toggle" />
      </div>
      <p className="text-xs text-muted-foreground">When enabled, clients can favorite photos for editing and leave per-photo notes.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <In label="Included edits (from package)" type="number" value={form.included_edits} onChange={(v) => setForm({ ...form, included_edits: v })} testid="set-fav-included" />
        <In label="Extra edit price ($)" type="number" value={form.extra_edit_price} onChange={(v) => setForm({ ...form, extra_edit_price: v })} testid="set-fav-extra-price" />
      </div>
      <div className="flex justify-end pt-3 border-t border-border">
        <button
          onClick={() => save({ favorites_enabled: form.favorites_enabled, included_edits: Number(form.included_edits) || 0, extra_edit_price: Number(form.extra_edit_price) || 0 })}
          className="px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em]"
          data-testid="save-fav-btn"
        >Save</button>
      </div>
    </div>
  );
}

function Toggle({ on, onChange, testid }) {
  return (
    <button
      onClick={() => onChange(!on)}
      data-testid={testid}
      className={`relative w-12 h-6 rounded-full transition-colors ${on ? "bg-emerald-600" : "bg-stone-300"}`}
      aria-pressed={on}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${on ? "translate-x-6" : ""}`} />
    </button>
  );
}

/* ----------------- ACTIVITIES TAB ----------------- */
function ActivitiesTab({ g }) {
  return (
    <section className="bg-card border border-border p-6" data-testid="activities-tab">
      <h2 className="font-serif text-2xl mb-1">Activities</h2>
      <p className="text-xs text-muted-foreground mb-6">A summary of what&apos;s happened on this collection.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <Stat label="Total photos" value={g.photos?.length || 0} icon={ImageIcon} />
        <Stat label="Selections received" value={g.selections?.length || 0} icon={ListChecks} />
        <Stat label="Status" value={g.status} icon={CheckCircle2} />
      </div>
      <div className="mt-6">
        <h3 className="font-serif text-lg mb-3">Timeline</h3>
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-foreground" /> Created {new Date(g.created_at).toLocaleString()}</li>
          {g.sent_at && <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-foreground" /> Shared with client {new Date(g.sent_at).toLocaleString()}</li>}
          {g.selections?.length > 0 && <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-foreground" /> {g.selections.length} client submission{g.selections.length > 1 ? "s" : ""}</li>}
          {g.delivered_at && <li className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-emerald-600" /> Delivered {new Date(g.delivered_at).toLocaleString()}</li>}
        </ul>
      </div>
    </section>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="border border-border p-4">
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="font-serif text-3xl mt-2 capitalize">{value}</div>
    </div>
  );
}

/* ----------------- Selections inline ----------------- */
function SelectionsBlock({ g }) {
  const photoMap = Object.fromEntries(g.photos.map((p) => [p.id, p]));
  return (
    <div id="selections" className="mt-8 pt-6 border-t border-border scroll-mt-24" data-testid="selections-block">
      <div className="flex items-center gap-2 mb-4">
        <ListChecks className="w-4 h-4" />
        <h3 className="font-serif text-xl">Client selections</h3>
      </div>
      <div className="space-y-6">
        {g.selections.map((s) => {
          const commentsByPhoto = Object.fromEntries((s.items || []).map((it) => [it.photo_id, it.comment]));
          const totalsLine = (s.included_edits || 0) > 0
            ? <>{s.included_edits} included · {s.extra_edit_count || 0} extra × ${Number(s.extra_edit_price || 0).toFixed(2)} = <strong className="text-foreground">${Number(s.extra_edit_total || 0).toFixed(2)}</strong></>
            : null;
          return (
            <div key={s.id} className="border-t border-border pt-4 first:border-t-0 first:pt-0" data-testid={`selection-${s.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <div>
                  <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                    {s.client_name} {s.client_email && <span className="text-muted-foreground font-normal">· {s.client_email}</span>}
                    {s.superseded && (
                      <span
                        className="text-[9px] uppercase tracking-[0.22em] px-1.5 py-0.5 border border-border text-muted-foreground"
                        title="The client submitted a newer list — this earlier batch is kept for reference"
                        data-testid={`selection-superseded-${s.id}`}
                      >
                        Replaced by newer
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{new Date(s.submitted_at).toLocaleString()} · {s.photo_ids.length} favorited</div>
                  {totalsLine && <div className="text-xs text-muted-foreground mt-0.5">{totalsLine}</div>}
                </div>
                <button
                  onClick={() => {
                    const names = s.photo_ids
                      .map((pid) => photoMap[pid]?.original_filename)
                      .filter(Boolean)
                      .join("\n");
                    const n = names.split("\n").length;
                    copyToClipboardWithToast(names, `Copied ${n} filename${n > 1 ? "s" : ""} to clipboard`);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 h-8 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60 hover:bg-muted shrink-0"
                  data-testid={`copy-filenames-${s.id}`}
                  title="Copy every favorited filename — paste into Finder/Explorer search to locate your raw files"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy filenames
                </button>
              </div>
              {s.message && <div className="text-sm italic text-muted-foreground mb-3">&ldquo;{s.message}&rdquo;</div>}
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                {s.photo_ids.map((pid) => {
                  const photo = photoMap[pid];
                  if (!photo) return null;
                  const comment = commentsByPhoto[pid];
                  const fname = photo.original_filename || "";
                  return (
                    <div key={pid} className="bg-card border border-border overflow-hidden" data-testid={`selection-photo-${pid}`}>
                      <div className="aspect-square bg-muted relative">
                        <img src={`${FILE_BASE}/api/files/${photo.file_id}?w=600&q=78`} alt={fname} className="w-full h-full object-cover" loading="lazy" />
                        <Heart className="w-4 h-4 absolute bottom-1.5 right-1.5 text-rose-500 fill-rose-500" />
                      </div>
                      {fname && (
                        <button
                          onClick={() => copyToClipboardWithToast(fname, "Filename copied")}
                          className="w-full text-left px-2 py-1.5 text-[11px] font-mono truncate hover:bg-muted border-t border-border flex items-center gap-1"
                          data-testid={`copy-filename-${pid}`}
                          title={`Click to copy "${fname}"`}
                        >
                          <Copy className="w-3 h-3 text-muted-foreground shrink-0" />
                          <span className="truncate">{fname}</span>
                        </button>
                      )}
                      {comment && (
                        <div className="p-2 text-[11px] italic border-t border-border">
                          <MessageSquare className="w-3 h-3 inline mr-1 text-muted-foreground" /> &ldquo;{comment}&rdquo;
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function In({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
        data-testid={testid}
      />
    </label>
  );
}


/* ----------------- FOLDERS SECTION ----------------- */
function FoldersSection({ g, reload }) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const folders = g.folders || [];

  if (folders.length === 0 && !creating) {
    return (
      <div className="bg-card border border-border px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="folders-empty">
        <div className="flex items-center gap-3">
          <FolderPlus className="w-4 h-4 text-muted-foreground" />
          <div>
            <div className="text-sm font-medium">Organize with folders</div>
            <div className="text-xs text-muted-foreground">
              Create folders inside this collection to share edited photos separately, or split by event day.
            </div>
          </div>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-4 h-9 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60"
          data-testid="add-folder-btn-empty"
        >
          <Plus className="w-3.5 h-3.5" /> New folder
        </button>
        <NewFolderDialog open={creating} onClose={() => setCreating(false)} parentId={g.id} existingCount={folders.length} onCreated={() => { setCreating(false); reload(); }} />
      </div>
    );
  }

  return (
    <section data-testid="folders-section" className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-2 inline-flex items-center gap-1.5">
            <Folder className="w-3 h-3" /> Folders · {folders.length}
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight">
            Inside this collection.
          </h2>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-4 h-10 border border-border hover:border-foreground/60 text-[11px] uppercase tracking-[0.22em] transition-colors"
          data-testid="add-folder-btn"
        >
          <Plus className="w-3.5 h-3.5" /> New folder
        </button>
      </div>

      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {folders.map((f) => (
          <button
            key={f.id}
            onClick={() => navigate(`/admin/galleries/${f.id}`)}
            className="text-left bg-card border border-border overflow-hidden hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-0.5 transition-all duration-500 group relative"
            data-testid={`folder-card-${f.id}`}
          >
            {(f.selection_count || 0) > 0 && (
              <span className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2 py-0.5 bg-rose-500 text-white text-[10px] uppercase tracking-[0.22em]">
                <Heart className="w-3 h-3 fill-white" /> {f.selection_count}
              </span>
            )}
            <div className="aspect-[4/5] bg-muted overflow-hidden relative">
              {f.cover_image ? (
                <img
                  src={`${FILE_BASE}${f.cover_image}`}
                  alt={f.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-muted/60 to-muted/20 flex items-center justify-center">
                  <Folder className="w-7 h-7 text-muted-foreground/40" strokeWidth={1.25} />
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/[0.06] transition-colors pointer-events-none" />
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Folder className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <div className="font-serif text-base leading-snug truncate">{f.name}</div>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground flex-wrap">
                <FolderStatusDot status={f.status} />
                <span className="capitalize">{(f.status || "draft").replace("_", " ")}</span>
                <span className="text-muted-foreground/40">·</span>
                <span>{f.photo_count || 0} {f.photo_count === 1 ? "photo" : "photos"}</span>
                {f.password && (
                  <>
                    <span className="text-muted-foreground/40">·</span>
                    <Lock className="w-3 h-3" />
                  </>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      <NewFolderDialog open={creating} onClose={() => setCreating(false)} parentId={g.id} existingCount={folders.length} onCreated={() => { setCreating(false); reload(); }} />
    </section>
  );
}

function FolderStatusDot({ status }) {
  const TONE = {
    draft: "bg-stone-300",
    sent: "bg-[#C9A961]",
    selections_received: "bg-rose-500",
    delivered: "bg-emerald-600/80",
    archived: "bg-stone-500",
  };
  return (
    <span
      className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${TONE[status] || TONE.draft}`}
      data-testid={`folder-status-dot-${status || "draft"}`}
    />
  );
}

function NewFolderDialog({ open, onClose, parentId, onCreated, existingCount = 0 }) {
  const navigate = useNavigate();
  // Two presets that match the photographer's actual workflow.
  // The default selection auto-flips based on whether this is the 1st or 2nd folder.
  const PRESETS = {
    selections: {
      id: "selections",
      icon: Heart,
      name: "Client Selections",
      tagline: "Client picks the photos they want edited",
      body: "Best for the first folder. Clients see the proofs, tap hearts on the ones they want edited, and submit. Downloads on with auto-password so they can save high-res copies too.",
      defaults: { name: "Client Selections", favorites_enabled: true, downloads_enabled: true, mode: "proofing" },
    },
    edited: {
      id: "edited",
      icon: Sparkles,
      name: "Edited Photos",
      tagline: "Share the finished, edited gallery",
      body: "Best for the second folder. Final retouched photos clients can browse and download with the password you'll get after creating.",
      defaults: { name: "Edited Photos", favorites_enabled: false, downloads_enabled: true, mode: "delivery" },
    },
  };

  // Auto-pick the right preset for this slot
  const defaultPreset = existingCount === 0 ? "selections" : "edited";
  const [presetId, setPresetId] = useState(defaultPreset);
  const [name, setName] = useState(PRESETS[defaultPreset].defaults.name);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const p = existingCount === 0 ? "selections" : "edited";
      setPresetId(p);
      setName(PRESETS[p].defaults.name);
      setSubmitting(false);
    }
  }, [open, existingCount]);

  function pickPreset(id) {
    setPresetId(id);
    setName(PRESETS[id].defaults.name);
  }

  async function submit() {
    if (submitting) return;
    if (!name.trim()) return toast.error("Folder name is required");
    setSubmitting(true);
    try {
      const preset = PRESETS[presetId];
      // Step 1: create the folder. Backend auto-generates both passwords because
      // we omit them in the payload. Folder is created with default flags;
      // step 2 below applies the preset's flag overrides.
      const { data } = await api.post("/admin/galleries", {
        name: name.trim(),
        parent_gallery_id: parentId,
      });
      // Step 2: patch the preset's flag overrides (favorites / downloads / mode).
      const patches = {};
      if (preset.defaults.favorites_enabled !== undefined && preset.defaults.favorites_enabled !== data.favorites_enabled) {
        patches.favorites_enabled = preset.defaults.favorites_enabled;
      }
      if (preset.defaults.downloads_enabled !== undefined && preset.defaults.downloads_enabled !== data.downloads_enabled) {
        patches.downloads_enabled = preset.defaults.downloads_enabled;
      }
      if (preset.defaults.mode && preset.defaults.mode !== data.mode) {
        patches.mode = preset.defaults.mode;
      }
      if (Object.keys(patches).length > 0) {
        await api.patch(`/admin/galleries/${data.id}`, patches);
      }
      toast.success(`Folder "${data.name}" created — password ready`);
      onCreated();
      navigate(`/admin/galleries/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light flex items-center gap-2">
            <FolderPlus className="w-5 h-5" /> New folder
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5" data-testid="new-folder-form">
          {/* Preset picker */}
          <div className="space-y-2">
            <div className="eyebrow">What&apos;s this folder for?</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.values(PRESETS).map((p) => {
                const Icon = p.icon;
                const active = p.id === presetId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => pickPreset(p.id)}
                    className={`text-left p-4 border-2 transition-all ${active ? "border-foreground bg-muted/40" : "border-border hover:border-foreground/40"}`}
                    data-testid={`folder-preset-${p.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon className={`w-4 h-4 ${active ? "text-foreground" : "text-muted-foreground"}`} />
                      <div className="font-medium text-sm">{p.name}</div>
                    </div>
                    <div className="text-xs text-muted-foreground mb-2">{p.tagline}</div>
                    <div className="text-[11px] text-muted-foreground/80 leading-relaxed">{p.body}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <In label="Folder name (edit if you like)" value={name} onChange={setName} testid="folder-name" />

          <div className="bg-muted/30 border border-border p-3 text-xs text-muted-foreground flex items-start gap-2">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              A unique <strong>view password</strong> and <strong>download password</strong> will be generated automatically.
              You&apos;ll see both at the top of the folder page — ready to copy & send to your client.
            </span>
          </div>

          <DialogFooter className="gap-2">
            <button onClick={onClose} className="px-5 h-10 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60">Cancel</button>
            <button
              onClick={submit}
              disabled={submitting}
              className="px-5 h-10 text-xs uppercase tracking-[0.18em] bg-primary text-primary-foreground disabled:opacity-60"
              data-testid="create-folder-btn"
            >
              {submitting ? "Creating…" : "Create folder"}
            </button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------- SHARE KIT PANEL (folder pages) -----------------
   Shows the public link + auto-generated view & download passwords, with
   one-click copy buttons + a "Copy share kit" master button that puts a
   nicely-formatted text block on the clipboard ready to paste into an
   email/SMS for the client. */
function ShareKitPanel({ g }) {
  const [showView, setShowView] = useState(false);
  const [showDl, setShowDl] = useState(false);

  // "Send via email" dialog state. Pre-fills recipient with the gallery's
  // existing customer_email when set so the photographer just hits send,
  // but they can also edit the address for one-off forwards.
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailNote, setEmailNote] = useState("");
  const [sending, setSending] = useState(false);

  function openEmailDialog() {
    setEmailTo(g.customer_email || "");
    setEmailNote("");
    setEmailDialogOpen(true);
  }

  async function sendShareEmail() {
    const to = (emailTo || "").trim();
    if (!to) {
      toast.error("Add a recipient email");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/galleries/${g.id}/send`, {
        to,
        note: emailNote.trim() || undefined,
      });
      toast.success(`Share kit sent to ${data.sent_to || to}`);
      setEmailDialogOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send email");
    } finally {
      setSending(false);
    }
  }

  const folderLabel = g.name || "Gallery";
  const lines = [
    `${folderLabel}`,
    `Link: ${g.share_url}`,
  ];
  if (g.password) lines.push(`Password: ${g.password}`);
  if (g.download_password && g.downloads_enabled !== false) lines.push(`Download password: ${g.download_password}`);
  const blockText = lines.join("\n");

  return (
    <section
      className="bg-gradient-to-br from-card to-muted/30 border border-border p-5 space-y-4"
      data-testid="share-kit-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Share kit
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            Send your client this link and the password{g.download_password && g.downloads_enabled !== false ? "s" : ""} so they can {g.favorites_enabled ? "pick their favorites" : "view & download"}.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={openEmailDialog}
            className="inline-flex items-center gap-2 px-4 h-10 border border-foreground hover:bg-foreground hover:text-background text-xs uppercase tracking-[0.18em] transition-colors"
            data-testid="email-share-kit-btn"
            title="Email this share kit directly to your client"
          >
            <Mail className="w-4 h-4" /> Email it
          </button>
          <button
            onClick={() => copyToClipboardWithToast(blockText, "Share kit copied — paste it into your message")}
            className="inline-flex items-center gap-2 px-4 h-10 bg-foreground text-background text-xs uppercase tracking-[0.18em] hover:bg-foreground/90"
            data-testid="copy-share-kit-btn"
          >
            <Copy className="w-4 h-4" /> Copy
          </button>
        </div>
      </div>

      {/* Link row */}
      <ShareKitRow
        label="Link"
        icon={ExternalLink}
        value={g.share_url}
        masked={false}
        testid="sk-link"
        trailing={
          <a
            href={g.share_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-8 h-8 border border-border hover:border-foreground/60"
            title="Open in new tab"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        }
      />

      {/* View password row — only if present (folders always have one; top-level may not) */}
      {g.password && (
        <ShareKitRow
          label="View password"
          icon={Lock}
          value={g.password}
          masked={!showView}
          onToggleMask={() => setShowView((v) => !v)}
          testid="sk-view-pw"
        />
      )}

      {/* Download password row — only if downloads enabled */}
      {g.download_password && g.downloads_enabled !== false && (
        <ShareKitRow
          label="Download password"
          icon={Download}
          value={g.download_password}
          masked={!showDl}
          onToggleMask={() => setShowDl((v) => !v)}
          testid="sk-dl-pw"
        />
      )}

      {!g.password && !g.download_password && (
        <div className="text-xs text-muted-foreground italic">
          No passwords set — this gallery is publicly accessible via the link.
        </div>
      )}

      {/* ── Email-to-client dialog ──────────────────────────────────────
          Sends the full styled share-kit email (cover, intro, branded CTA,
          password block, optional handwritten note) from your verified
          Lumetier sender to whoever you specify. */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="email-share-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#C9A961]" />
              Email this share kit
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              We&apos;ll send a styled email from your studio with the gallery link,
              password{g.download_password && g.downloads_enabled !== false ? "s" : ""}, and any personal note you add.
            </p>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground font-sans block mb-2">
                Send to
              </label>
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="client@example.com"
                autoFocus
                className="w-full border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:border-foreground transition-colors"
                data-testid="email-share-to-input"
              />
              {g.customer_email && emailTo !== g.customer_email && (
                <button
                  type="button"
                  onClick={() => setEmailTo(g.customer_email)}
                  className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground mt-2 transition-colors"
                >
                  Use client email on file ({g.customer_email})
                </button>
              )}
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground font-sans flex items-center justify-between gap-3 mb-2">
                <span>Personal note <span className="normal-case tracking-normal text-muted-foreground/70 ml-1">(optional)</span></span>
                <DraftWithAIButton
                  purpose="share_note"
                  context={`Gallery: ${g.name || "(untitled)"}${g.customer_name ? ` · Client: ${g.customer_name}` : ""}`}
                  onDraft={(text) => setEmailNote(text.slice(0, 600))}
                  testid="draft-ai-share-note"
                />
              </label>
              <textarea
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)}
                placeholder="Hi Sarah — your favorites from Saturday are ready. Take your time picking; let me know if anything stands out."
                rows={4}
                maxLength={600}
                className="w-full border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:border-foreground transition-colors resize-none leading-relaxed"
                data-testid="email-share-note-input"
              />
              <div className="text-[10px] text-muted-foreground mt-1 text-right tabular-nums">
                {emailNote.length}/600
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <button
              type="button"
              onClick={() => setEmailDialogOpen(false)}
              disabled={sending}
              className="inline-flex items-center justify-center px-4 h-10 border border-border hover:border-foreground/60 text-xs uppercase tracking-[0.18em] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={sendShareEmail}
              disabled={sending || !emailTo.trim()}
              data-testid="email-share-send-btn"
              className="inline-flex items-center justify-center gap-2 px-4 h-10 bg-foreground text-background text-xs uppercase tracking-[0.18em] hover:bg-foreground/90 disabled:opacity-50"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {sending ? "Sending" : "Send share kit"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ShareKitRow({ label, icon: Icon, value, masked, onToggleMask, testid, trailing }) {
  const display = masked ? "•".repeat(Math.max(6, value.length)) : value;
  return (
    <div className="flex items-center gap-3 bg-background border border-border px-3 py-2.5">
      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
        <code className="font-mono text-sm text-foreground truncate block" data-testid={`${testid}-value`}>
          {display}
        </code>
      </div>
      {onToggleMask && (
        <button
          onClick={onToggleMask}
          className="inline-flex items-center justify-center w-8 h-8 border border-border hover:border-foreground/60"
          title={masked ? "Show" : "Hide"}
          data-testid={`${testid}-toggle`}
        >
          {masked ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </button>
      )}
      <button
        onClick={() => copyToClipboardWithToast(value, `${label} copied`)}
        className="inline-flex items-center justify-center w-8 h-8 border border-border hover:border-foreground/60"
        title={`Copy ${label.toLowerCase()}`}
        data-testid={`${testid}-copy`}
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
      {trailing}
    </div>
  );
}



/* ----------------- COLLABORATORS TAB (Phase G2 — interactive only) ----------------- */
function CollaboratorsTab({ g }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/galleries/${g.id}/collaborators`);
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [g.id]);
  useEffect(() => { load(); }, [load]);

  async function invitePrimary() {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/galleries/${g.id}/invite-primary`);
      if (data.email_status === "sent") {
        toast.success(`Magic link sent to ${data.sent_to}`);
      } else {
        toast.warning(`Couldn't deliver email — link copied to clipboard instead`);
      }
      try { await copyToClipboardWithToast(data.magic_link, "Magic link copied"); } catch (err) { void err; }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  }

  async function revoke(cid) {
    try {
      await api.delete(`/admin/galleries/${g.id}/collaborators/${cid}`);
      toast.success("Collaborator revoked");
      setConfirmRevoke(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  if (!rows) return <div className="text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading collaborators…</div>;

  const primary = rows.find((r) => r.role === "primary");
  const others = rows.filter((r) => r.role !== "primary");

  return (
    <section className="bg-card border border-border p-6 space-y-6" data-testid="collaborators-tab">
      <header className="flex items-center justify-between gap-3 pb-3 border-b border-border">
        <div>
          <h3 className="font-serif text-2xl font-light tracking-tight">Collaborators</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Identity layer for this interactive gallery. The primary client is auto-provisioned from the booking; friends join via the share link.
          </p>
        </div>
        <button
          onClick={invitePrimary}
          disabled={busy || !primary}
          className="inline-flex items-center gap-2 px-4 h-10 bg-foreground text-background text-[11px] uppercase tracking-[0.22em] disabled:opacity-40 hover:opacity-90 transition-opacity"
          data-testid="invite-primary-btn"
        >
          <Mail className="w-3.5 h-3.5" /> {primary?.verified_at ? "Resend primary link" : "Send primary invite"}
        </button>
      </header>

      {!primary && (
        <div className="border border-amber-400/60 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="no-primary-warning">
          No primary collaborator yet. Set a client email in <strong>Settings → General</strong> (or link a booking with one) and save — the primary will be auto-provisioned.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-muted-foreground border-b border-border">
              <th className="py-2 pr-3">Name</th>
              <th className="py-2 pr-3">Email</th>
              <th className="py-2 pr-3">Role</th>
              <th className="py-2 pr-3">Joined</th>
              <th className="py-2 pr-3">Last seen</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {[...(primary ? [primary] : []), ...others].map((r) => (
              <tr key={r.id} className="border-b border-border/60 last:border-0" data-testid={`collaborator-row-${r.id}`}>
                <td className="py-3 pr-3 font-medium">{r.display_name}</td>
                <td className="py-3 pr-3 text-muted-foreground">{r.email}</td>
                <td className="py-3 pr-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] ${r.role === "primary" ? "bg-foreground text-background" : "bg-muted text-muted-foreground"}`}>
                    {r.role}
                  </span>
                </td>
                <td className="py-3 pr-3 text-xs text-muted-foreground">
                  {r.joined_at ? new Date(r.joined_at).toLocaleDateString() : "—"}
                </td>
                <td className="py-3 pr-3 text-xs text-muted-foreground">
                  {r.last_seen_at ? new Date(r.last_seen_at).toLocaleDateString() : "—"}
                </td>
                <td className="py-3 pr-3">
                  {r.verified_at
                    ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[#5F7A6F]"><CheckCircle2 className="w-3 h-3" /> Verified</span>
                    : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-amber-700">Pending</span>}
                </td>
                <td className="py-3 pr-3 text-right">
                  {r.role === "primary" && r.collaborator_token && (
                    <button
                      onClick={() => copyToClipboardWithToast(
                        `${g.share_url}?primary_token=${r.collaborator_token}`,
                        "Magic link copied",
                      )}
                      className="inline-flex items-center gap-1 px-2 h-7 text-[10px] uppercase tracking-[0.18em] border border-border hover:border-foreground/60 mr-1"
                      data-testid={`copy-magic-${r.id}`}
                      title="Copy magic link"
                    >
                      <Copy className="w-3 h-3" /> Link
                    </button>
                  )}
                  <button
                    onClick={() => setConfirmRevoke(r)}
                    className="inline-flex items-center gap-1 px-2 h-7 text-[10px] uppercase tracking-[0.18em] border border-destructive/40 text-destructive hover:bg-destructive/5"
                    data-testid={`revoke-${r.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="py-10 text-center text-sm text-muted-foreground">No collaborators yet.</div>
        )}
      </div>

      {confirmRevoke && (
        <Dialog open onOpenChange={() => setConfirmRevoke(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Revoke {confirmRevoke.display_name}?</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">
              Their access will be cut immediately. Any favorites or notes they've left remain attributed to their name.
            </p>
            <DialogFooter>
              <button onClick={() => setConfirmRevoke(null)} className="px-4 h-9 border border-border text-xs uppercase tracking-[0.18em]">Cancel</button>
              <button onClick={() => revoke(confirmRevoke.id)} className="px-4 h-9 bg-destructive text-white text-xs uppercase tracking-[0.18em]" data-testid="confirm-revoke">Revoke</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}

/* ----------------- ADMIN FAVORITES TAB (Phase G2 — interactive only) ----------------- */
function AdminFavoritesTab({ g }) {
  const [view, setView] = useState("combined");  // combined | per_collaborator
  const [data, setData] = useState(null);
  const [selectedCid, setSelectedCid] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/galleries/${g.id}/favorites?view=${view}`);
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [g.id, view]);
  useEffect(() => { load(); }, [load]);

  const photoMap = useMemo(() => {
    const m = {};
    for (const p of (g.photos || [])) m[p.id] = p;
    return m;
  }, [g.photos]);

  const collaboratorRows = view === "per_collaborator" ? (data?.collaborators || []) : [];
  const activeCollab = view === "per_collaborator"
    ? collaboratorRows.find((c) => c.collaborator_id === selectedCid) || collaboratorRows[0]
    : null;

  const heatmapPhotos = view === "combined" ? (data?.photos || []) : [];
  const maxCount = heatmapPhotos[0]?.count || 1;

  // Filenames the copy-all button will hit the clipboard with, respecting
  // the current view + the sort order the user is looking at.
  const filenamesToCopy = useMemo(() => {
    if (view === "combined") {
      return heatmapPhotos
        .map((p) => photoMap[p.photo_id]?.original_filename)
        .filter(Boolean);
    }
    if (view === "per_collaborator" && activeCollab) {
      return (activeCollab.photo_ids || [])
        .map((pid) => photoMap[pid]?.original_filename)
        .filter(Boolean);
    }
    return [];
  }, [view, heatmapPhotos, activeCollab, photoMap]);

  const copyAllFilenames = () => {
    if (filenamesToCopy.length === 0) {
      toast.error("No favorites to copy yet.");
      return;
    }
    navigator.clipboard?.writeText(filenamesToCopy.join("\n"));
    toast.success(`Copied ${filenamesToCopy.length} filename${filenamesToCopy.length === 1 ? "" : "s"}`);
  };

  return (
    <section className="bg-card border border-border p-6 space-y-6" data-testid="admin-favorites-tab">
      <header className="flex items-center justify-between flex-wrap gap-3 pb-3 border-b border-border">
        <div>
          <h3 className="font-serif text-2xl font-light tracking-tight">Favorites</h3>
          <p className="text-xs text-muted-foreground mt-1">Aggregate picks across all collaborators. Read-only.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center bg-muted/60 p-0.5 rounded-sm">
            <button
              onClick={() => setView("combined")}
              className={`px-3 h-8 text-[10px] uppercase tracking-[0.22em] ${view === "combined" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="admin-fav-view-combined"
            >Combined heatmap</button>
            <button
              onClick={() => setView("per_collaborator")}
              className={`px-3 h-8 text-[10px] uppercase tracking-[0.22em] ${view === "per_collaborator" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="admin-fav-view-percol"
            >Per collaborator</button>
          </div>
          <button
            onClick={copyAllFilenames}
            disabled={filenamesToCopy.length === 0}
            title={filenamesToCopy.length === 0
              ? "No favorites yet"
              : `Copy ${filenamesToCopy.length} filename${filenamesToCopy.length === 1 ? "" : "s"} · paste straight into Lightroom's filter or a text file`}
            className={`inline-flex items-center gap-1.5 px-3 h-8 text-[10px] uppercase tracking-[0.22em] border ${filenamesToCopy.length === 0
              ? "border-border text-muted-foreground opacity-50 cursor-not-allowed"
              : "border-border hover:border-foreground/60"}`}
            data-testid="copy-all-filenames-btn"
          >
            <Copy className="w-3 h-3" />
            Copy all filenames
          </button>
          <button
            disabled={g.status !== "selections_received"}
            onClick={() => {
              if (g.status !== "selections_received") return;
              // The existing admin gallery payload already includes `selections` —
              // jump to the Photos tab where they're rendered (no separate route yet).
              const el = document.querySelector('[data-testid="tab-photos"]');
              if (el) el.click();
            }}
            title={g.status === "selections_received" ? "Open the submitted selection" : "Waiting for the client to submit their selection."}
            className={`inline-flex items-center gap-1.5 px-3 h-8 text-[10px] uppercase tracking-[0.22em] border ${g.status === "selections_received" ? "border-foreground bg-foreground text-background hover:opacity-90" : "border-border text-muted-foreground opacity-50 cursor-not-allowed"}`}
            data-testid="export-selection-btn"
          >
            <Download className="w-3 h-3" />
            {g.status === "selections_received" ? "Open submitted selection" : "Export selection"}
          </button>
        </div>
      </header>

      {!data && <div className="text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>}

      {view === "combined" && data && (
        heatmapPhotos.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No favorites yet.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="favorites-heatmap-grid">
            {heatmapPhotos.map((p, i) => {
              const photo = photoMap[p.photo_id];
              if (!photo) return null;
              const intensity = p.count / maxCount;
              return (
                <div key={p.photo_id} className="relative group bg-muted aspect-square overflow-hidden" data-testid={`fav-photo-${p.photo_id}`}>
                  <img src={`${FILE_BASE}/api/files/${photo.file_id}?w=400&q=75`} alt={photo.original_filename} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 ring-2 ring-inset" style={{ borderColor: "transparent", boxShadow: `inset 0 0 0 ${Math.max(2, intensity * 8)}px rgba(201,169,97,${0.35 + intensity * 0.55})` }} />
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-stone-900/85 text-[#F5EFE0] text-[10px] tracking-[0.18em] uppercase">
                    #{i + 1} · <span className="text-[#C9A961]">{p.count}</span>
                  </div>
                  {/* Filename ribbon — always visible so the photographer can
                      match each favorite to the original file in their editor.
                      Click-to-copy for quick paste into Lightroom / Photoshop. */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard?.writeText(photo.original_filename || "");
                      toast.success(`Copied ${photo.original_filename}`);
                    }}
                    title={`Click to copy · ${photo.original_filename}`}
                    className="absolute bottom-0 inset-x-0 bg-stone-900/85 hover:bg-stone-900 text-[#F5EFE0] px-2 py-1 text-left transition-colors"
                    data-testid={`fav-photo-filename-${p.photo_id}`}
                  >
                    <div className="text-[10px] font-mono truncate" title={photo.original_filename}>
                      {photo.original_filename || "—"}
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.22em] text-[#C9A961]/80 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">
                      Picked by {(p.collaborators || []).map((c) => c.display_name).join(", ") || "—"}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        )
      )}

      {view === "per_collaborator" && data && (
        collaboratorRows.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No favorites yet.</div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {collaboratorRows.map((c) => {
                const active = (selectedCid || collaboratorRows[0].collaborator_id) === c.collaborator_id;
                return (
                  <button
                    key={c.collaborator_id}
                    onClick={() => setSelectedCid(c.collaborator_id)}
                    className={`inline-flex items-center gap-1.5 px-3 h-8 text-[11px] uppercase tracking-[0.18em] ${active ? "bg-foreground text-background" : "border border-border hover:border-foreground/60"}`}
                    data-testid={`per-col-${c.collaborator_id}`}
                  >
                    {c.display_name} <span className="opacity-60">· {c.photo_ids.length}</span>
                  </button>
                );
              })}
            </div>
            {activeCollab && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="per-collaborator-grid">
                {activeCollab.photo_ids.map((pid) => {
                  const photo = photoMap[pid];
                  if (!photo) return null;
                  return (
                    <div key={pid} className="relative group bg-muted aspect-square overflow-hidden">
                      <img src={`${FILE_BASE}/api/files/${photo.file_id}?w=400&q=75`} alt={photo.original_filename} loading="lazy" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard?.writeText(photo.original_filename || "");
                          toast.success(`Copied ${photo.original_filename}`);
                        }}
                        title={`Click to copy · ${photo.original_filename}`}
                        className="absolute bottom-0 inset-x-0 bg-stone-900/85 hover:bg-stone-900 text-[#F5EFE0] px-2 py-1 text-left text-[10px] font-mono truncate transition-colors"
                        data-testid={`per-col-fav-filename-${pid}`}
                      >
                        {photo.original_filename || "—"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )
      )}
    </section>
  );
}
